import { useCallback, useEffect, useRef, useState } from 'react';
import userSettingsService from '../services/userSettings.service';

const SETTING_KEY = 'navigation_order';

interface NavigationOrderValue {
  order: string[];
  groups: Record<string, string[]>;
}

export function useNavigationOrder() {
  const [value, setValue] = useState<NavigationOrderValue>({ order: [], groups: {} });
  const valueRef = useRef(value);
  const savingRef = useRef(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const setting = await userSettingsService.getUserSetting(SETTING_KEY);
        if (cancelled) return;
        const value = setting?.setting_value as NavigationOrderValue | undefined;
        valueRef.current = { order: value?.order ?? [], groups: value?.groups ?? {} };
        setValue(valueRef.current);
      } catch {
        if (!cancelled) setValue({ order: [], groups: {} });
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const save = useCallback(async (update: Partial<NavigationOrderValue>) => {
    if (savingRef.current) return;
    const previous = valueRef.current;
    const next = { ...previous, ...update };
    savingRef.current = true;
    setIsSaving(true);
    valueRef.current = next;
    setValue(next);
    try {
      await userSettingsService.saveUserSetting(SETTING_KEY, next);
    } catch (err) {
      valueRef.current = previous;
      setValue(previous);
      throw err;
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  }, []);

  const setOrder = useCallback((order: string[]) => save({ order }), [save]);
  const setGroupOrder = useCallback((group: string, order: string[]) =>
    save({ groups: { ...valueRef.current.groups, [group]: order } }), [save]);

  return { ...value, isLoading, isSaving, setOrder, setGroupOrder };
}

// Orders items by the saved order (stable for items present in `order`), then
// appends any items whose `name` is not in `order` in their original relative
// order. This makes newly-added sections show up at the bottom without
// clobbering the user's preference.
export function applyNavOrder<T extends { name: string }>(
  items: T[],
  order: string[],
  key: (item: T) => string = item => item.name,
): T[] {
  if (order.length === 0) return items;
  const byName = new Map(items.map((item) => [key(item), item]));
  const seen = new Set<string>();
  const ordered: T[] = [];
  for (const name of order) {
    const item = byName.get(name);
    if (item && !seen.has(name)) {
      ordered.push(item);
      seen.add(name);
    }
  }
  for (const item of items) {
    if (!seen.has(key(item))) {
      ordered.push(item);
      seen.add(key(item));
    }
  }
  return ordered;
}
